import uri from 'urijs';
import { flatMap, filter } from 'lodash';
import { EndPoint, getURL, Request, RequestType } from '@model/service';
import { BRAND_SUFFIXES } from '@model/iceberg/service/algolia/AlgoliaApi';
import { EnvConfig } from '@model/config/env-config';
import { AppVariant } from '@model/config/brands';
import { Continent, Country, Region, TripTypes } from '@model/common';
import { NavDestinations, PageConfig, PageData, PageTemplates, ResortLanding, ListType } from '@model/content';

interface ContentApiRequests {
  getNavDestinations: () => Promise<NavDestinations>;
  getGeography: (path?: string) => Promise<Array<Continent>>;
  getCountry: (path: string) => Promise<Array<Country>>;
  getDestination: (path: string) => Promise<Array<Country> | Array<Region>>;
  getResort: (path: string) => Promise<ResortLanding>;
  getPageConfigMap: ({ template }: PageConfigParams) => Promise<Array<PageData>>;
  getPageConfig: ({ template, path, tripType }: PageConfigParams) => Promise<PageConfig>;
}

interface PageConfigParams {
  template: PageTemplates;
  path?: string;
  tripType?: TripTypes;
  listType?: ListType;
}

export class ContentApi implements ContentApiRequests {
  private api: Request = new Request();

  public async getNavDestinations(): Promise<NavDestinations> {
    return await this.api
      .get(uri(getURL(RequestType.CONTENT)).path(EndPoint.CONTENT.concat(EndPoint.DESTINATION_MENU)).href())
      .then(({ data }) => data);
  }

  public async getGeography(): Promise<Array<Continent>> {
    return await this.api.get(
      uri(getURL(RequestType.CONTENT)).path(EndPoint.CONTENT.concat(EndPoint.GEOGRAPHY_DESTINATIONS)).href()
    );
  }

  public async getCountry(path: string): Promise<Array<Country>> {
    return await this.api.get(
      uri(getURL(RequestType.CONTENT))
        .path(EndPoint.CONTENT.concat(EndPoint.GEOGRAPHY_DESTINATIONS))
        .search({ path })
        .href()
    );
  }

  public async getDestinations(): Promise<Array<Country | Region>> {
    const continents = await this.getGeography();
    const countries = flatMap(continents, 'countries');
    const regions = flatMap(countries, 'regions');
    return filter(countries.concat(regions), ({ isDestination, tripTypes }) => isDestination && tripTypes.length > 0);
  }

  public async getDestination(path: string): Promise<Array<Country> | Array<Region>> {
    return await this.api.get(
      uri(getURL(RequestType.CONTENT))
        .path(EndPoint.CONTENT.concat(EndPoint.GEOGRAPHY_DESTINATIONS))
        .search({ path })
        .href()
    );
  }

  public async getResort(path: string): Promise<ResortLanding> {
    return await this.api
      .get(uri(getURL()).path(EndPoint.CMS.concat(EndPoint.RESORT_LANDING_PAGE)).search({ path }).href())
      .catch(() => {
        return null;
      });
  }

  public async getCarousel(id: number | string) {
    return await this.api.get(
      uri(getURL(RequestType.CONTENT))
        .path(EndPoint.CONTENT.concat(EndPoint.CAROUSEL).concat(`/${id}`))
        .href()
    );
  }

  public async getPageConfigMap({ template }: PageConfigParams): Promise<Array<PageData>> {
    return await this.api
      .get(
        uri(getURL(RequestType.CONTENT))
          .path(EndPoint.CONTENT.concat(EndPoint.PAGE_CONFIG_MAP).concat(`/${template}`))
          .href()
      )
      .catch(() => {
        return null;
      });
  }

  public async getPageConfig({ template, path, tripType, listType }: PageConfigParams): Promise<PageConfig> {
    const isWhiteLabel = AppVariant.isWhitelabel();
    const tenant = isWhiteLabel ? BRAND_SUFFIXES[EnvConfig.get().APP_VARIANT_ID] : undefined;

    return await this.api
      .get(
        uri(getURL(RequestType.CONTENT))
          .path(EndPoint.CONTENT.concat(EndPoint.PAGE_CONFIG).concat(`/${template}`))
          .search({ path, tripType, listType, tenant })
          .href(),
        undefined,
        {
          suppressErrorLogs: true
        }
      )
      .catch(() => {
        return null;
      });
  }
}
